import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, getToken, setToken, setUnauthorizedHandler } from './api'

/**
 * Session state for the platform panel — a person who manages CLIENT
 * organisations, not a member of any one of them.
 *
 * Structurally close to the tenant app's own AuthProvider (same restore/
 * sign-in/sign-out shape), but genuinely simpler: no tenant, no permissions,
 * no branding to apply on restore.
 */

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null)
  const [restoring, setRestoring] = useState(() => Boolean(getToken()))

  const signOutLocally = useCallback(() => {
    setToken(null)
    setAdmin(null)
  }, [])

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null)
      setAdmin(null)
    })
  }, [])

  useEffect(() => {
    let cancelled = false

    async function restore() {
      if (!getToken()) {
        setRestoring(false)
        return
      }

      try {
        const data = await api.get('/platform/me')
        if (!cancelled) setAdmin(data.admin)
      } catch {
        if (!cancelled) signOutLocally()
      } finally {
        if (!cancelled) setRestoring(false)
      }
    }

    restore()

    return () => { cancelled = true }
  }, [signOutLocally])

  const signIn = useCallback(async ({ email, password }) => {
    const data = await api.post('/platform/login', { email, password, device: 'web' })

    setToken(data.token)
    setAdmin(data.admin)

    return data.admin
  }, [])

  // Called after a successful password change so `mustChangePassword` flips
  // immediately — without this, the context still held the admin object from
  // sign-in/restore, which said must_change_password: true forever, and
  // RequireAuth would bounce straight back to /change-password no matter
  // where the page tried to navigate next.
  const markPasswordChanged = useCallback(() => {
    setAdmin((current) => (current ? { ...current, must_change_password: false } : current))
  }, [])

  const signOut = useCallback(async () => {
    const revoking = getToken()

    signOutLocally()

    if (!revoking) return

    try {
      await api.post('/platform/logout', null, {
        headers: { Authorization: `Bearer ${revoking}` },
      })
    } catch {
      // Best-effort. The admin is already signed out locally, which is what
      // they asked for; the token expires on its own if this never lands.
    }
  }, [signOutLocally])

  const value = useMemo(() => ({
    admin,
    restoring,
    signIn,
    signOut,
    markPasswordChanged,
    isAuthenticated: Boolean(admin),
    mustChangePassword: Boolean(admin?.must_change_password),
  }), [admin, restoring, signIn, signOut, markPasswordChanged])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }

  return context
}
